// client/src/features/insurance/search.ts — SCR-INS-001's URL state · MAINT-009 (04 §9.2.3).
//
// §9.2.3 is NORMATIVE for every LIST screen: filters, pagination and sort live in the URL as
// Zod-validated search params, so a filtered register is shareable, survives a refresh, and Back
// is a filter-undo. `features/documents/search.ts` is the shape this copies.
//
// The names are `GET /api/v1/insurances`'s own — `propertyId`, `insuranceType`, `activeOn`,
// plus the 02 §7.4 paging — so the URL of the screen and the query string of the request are one
// vocabulary and `toListQuery` is a rename-free pass-through. `activeOn` is spelled `inForce` in
// the URL because the VALUE is not the user's: the control is a yes/no and the date it becomes
// is always today in Romania. The route file that ships this screen wrote the promise down —
// „when MAINT-009 gives the panel a «doar polițele în vigoare» control it becomes a §9.2.3
// schema" — and this is it.
//
// TWO PARAMS ARE NOT THE ENDPOINT'S. `view` picks between the REGISTER („ce polițe am") and the
// unified compliance CALENDAR („ce expiră, din toate sursele"), which is the two-renderings-of-
// one-screen shape `EquipmentRegister` and `DocumentArchive` both use; and the calendar's own
// five params arrive by spreading `complianceCalendarSearchSchema`, so the calendar can be
// re-homed onto SCR-FISC-002 in M4 by spreading the same object into that route's schema and
// changing nothing else (D-1132).
//
// `propertyId` is SHARED between the two renderings on purpose: „doar Aviației" means the same
// thing in the register and on the calendar, and carrying it across the toggle is what makes the
// toggle a view switch rather than a reset.

import { z } from 'zod';

import { complianceCalendarSearchSchema } from '@/features/compliance-calendar/search';
import { zUrlBoolean } from '@/lib/url-state';

import { INSURANCE_TYPES } from './enums';

/** The two renderings of SCR-INS-001. The register is the default: it answers „ce polițe am". */
export const INSURANCE_VIEWS = ['policies', 'calendar'] as const;

export type InsuranceView = (typeof INSURANCE_VIEWS)[number];

/**
 * Expiring first — `InsuranceStore.Order`'s own fallback („expiring first is what the panel of
 * 04 §8.11 opens on"), said out loud so the URL carries what the list is actually sorted by.
 */
export const DEFAULT_SORT = 'endDate:asc';

/** 02 §7.4 — `pageSize` default 25, max 200; the server refuses more rather than clamping. */
export const insuranceSearchSchema = z.object({
  ...complianceCalendarSearchSchema.shape,
  view: z.enum(INSURANCE_VIEWS).default('policies'),
  page: z.coerce.number().int().min(1).default(1),
  pageSize: z.coerce.number().int().min(1).max(200).default(25),
  sort: z.string().default(DEFAULT_SORT),
  insuranceType: z.enum(INSURANCE_TYPES).optional(),
  /**
   * „Doar polițele în vigoare" — the endpoint's `activeOn`, with today as its value. Tri-state
   * and read through `urlBoolean`, because the router parses search values with `JSON.parse`
   * and the same filter arrives as a boolean when shared and as a string when clicked (D-1197).
   */
  inForce: zUrlBoolean.optional(),
});

export type InsuranceSearch = z.infer<typeof insuranceSearchSchema>;

/** Which filters are engaged — the empty state needs to know why it is empty. */
export function hasActiveFilters(search: InsuranceSearch): boolean {
  return (
    search.propertyId !== undefined ||
    search.insuranceType !== undefined ||
    search.inForce !== undefined
  );
}

/** Clearing filters returns to page 1 and keeps the rendering the user chose. */
export function clearedSearch(search: InsuranceSearch): InsuranceSearch {
  return {
    view: search.view,
    calendar: search.calendar,
    month: search.month,
    horizon: search.horizon,
    page: 1,
    pageSize: search.pageSize,
    sort: search.sort,
  };
}
